import React from 'react';
import { Link } from 'react-router-dom';
import { Droplet, CheckCircle, FileText, ArrowRight, Shield, Truck, Building2, TrendingUp, Wind, Clock, Users } from 'lucide-react';

const OilAndGas = () => {
  const servicePillars = [
    {
      title: 'Portable Source Mastery (MG-1)',
      description: 'Rigorous management of the Minor General Permit 1. We track relocation notices and "non-road" engine status to ensure your drill rigs maintain their operational flexibility.',
      icon: Truck
    },
    {
      title: 'North Slope Support Strategy (MG-2)',
      description: 'Specialized oversight for Portable Oil and Gas Operations (POGO). We handle the nuances of the MG-2 permit for camps, ensuring auxiliary infrastructure never threatens your production timeline.',
      icon: Building2
    },
    {
      title: 'Strategic Source Aggregation Defense',
      description: 'Preventing the "Major Source" trap. We actively defend against EPA efforts to aggregate separate well pads into single Title V sources, protecting your independent status.',
      icon: Shield
    },
    {
      title: 'Quad O (OOOO/a/b/c) & Methane',
      description: 'Future-proofing against new "Super Emitter" rules. We manage LDAR (Leak Detection) and reporting for the latest NSPS Methane standards, keeping your facility compliant.',
      icon: Wind
    },
    {
      title: 'Construction Logistics & Ice Roads',
      description: 'Aligning permit issuance with logistics. We understand that if the permit isn\'t signed by the time the ice road opens, the project dies. We fast-track approvals to meet your window.',
      icon: Clock
    },
    {
      title: 'Agency Negotiation',
      description: 'Representation by former regulators. When we speak to ADEC or the EPA on your behalf, we speak their language—because we used to sit in their chairs.',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen" data-testid="oil-gas-page">
      {/* Hero Section - Oil & Gas with Responsive Image */}
      <section 
        className="hero-section relative text-white overflow-hidden"
        style={{
          height: '60vh',
          maxHeight: '720px',
          minHeight: '500px',
          padding: '5rem 0'
        }}
      >
        {/* Responsive Hero Image Container */}
        <div className="absolute inset-0 z-0">
          <style>{`
            .hero-section picture {
              width: 100%;
              height: 100%;
              display: block;
            }
            
            .hero-section .hero-image,
            .hero-section picture img {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              object-position: center center !important;
              display: block !important;
            }
            
            @media (max-width: 900px) {
              .hero-section {
                height: auto !important;
                max-height: none !important;
                min-height: 420px !important;
                padding: 3rem 0 !important;
              }
            }
          `}</style>
          
          <picture>
            <source 
              media="(min-width: 1600px)" 
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/neomxa9z_oil_gas_desktop_2560x1200.webp" />
            <source 
              media="(min-width: 900px)" 
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/1z8g3cma_oil_gas_tablet_1536x900.webp" />
            <source 
              media="(orientation: portrait)" 
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/6owj21wj_oil_gas_mobile_vertical_960x1200.webp" />
            <img 
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/dvjmnmxu_oil_gas_mobile_horizontal_960x640.webp"
              alt="Oil and gas infrastructure on the Alaska North Slope under the Northern Lights"
              className="hero-image"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }}
              loading="eager" />
          </picture>
          
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">
              We Didn't Just Read the Regulations. We Helped Write Them.
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-8 drop-shadow-md leading-relaxed">
              Navigating Alaska's General Permits (MG-1, MG-2, GP-1) requires more than a checklist. It requires understanding the regulator's intent. With a Principal Consultant who authored the State's MG-2 permit framework, Raven Environmental offers unmatched insight into compliance strategy.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-lg"
              >
                Leverage Insider Expertise
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The General Permit Strategy: Precision Matters</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              In the Alaskan oil patch, choosing the wrong permit class can cost you a season. Many operators conflate the MG-1, MG-2, and GP-1, leading to unnecessary restrictions.
            </p>
            
            <div className="space-y-4 mb-6">
              <div className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-600">
                <h3 className="font-bold text-gray-900 mb-2">MG-1 (Drill Rigs):</h3>
                <p className="text-gray-700">
                  The standard for portable drilling. We ensure your rig stays "mobile" in the eyes of the law, managing residency times to prevent it from becoming a stationary source.
                </p>
              </div>

              <div className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-600">
                <h3 className="font-bold text-gray-900 mb-2">MG-2 (North Slope POGO):</h3>
                <p className="text-gray-700">
                  The critical "Support & Camp" permit. Often overlooked, this covers the auxiliary equipment that keeps the crew alive. Our team offers unique authority here, having originally authored this permit's framework at ADEC.
                </p>
              </div>

              <div className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-600">
                <h3 className="font-bold text-gray-900 mb-2">GP-1 (General Permit):</h3>
                <p className="text-gray-700">
                  The foundational strategy for broader aggregate and support operations.
                </p>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed font-semibold">
              We don't guess which permit applies. We know the exact intent of the language because we have been on both sides of the desk.
            </p>
          </div>
        </div>
      </section>

      {/* Core Service Pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Core Service Pillars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicePillars.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-amber-600">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center mr-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Technical Specifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Box 1: Portable & Temporary Ops */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-amber-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Portable & Temporary Ops</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">MG-1 (Drilling Rigs) & MG-2 (North Slope POGO)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">GP-1 Strategy & Applicability</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Non-Road Engine Determinations (40 CFR 1068)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Relocation & Residency Tracking</span>
                </li>
              </ul>
            </div>

            {/* Box 2: Production & Stationary Ops */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-amber-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Production & Stationary Ops</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Title V & Synthetic Minor Limits</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NSPS OOOO/a/b/c (Methane & VOCs)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NESHAP Subpart HH (Glycol Dehydration)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">GHG Reporting (Subpart W)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory Requirements */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Key Regulatory Requirements</h2>
          <div className="space-y-8 max-w-5xl mx-auto">
            {/* NSPS */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 60 - New Source Performance Standards</h3>
              <p className="text-gray-600 mb-4">Oil and gas operations must comply with various NSPS subparts including:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart OOOO</h4>
                  <p className="text-sm text-gray-600">Standards for crude oil and natural gas production, transmission, and storage</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart OOOOa</h4>
                  <p className="text-sm text-gray-600">Updated standards for crude oil and natural gas facilities, including methane requirements</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart IIII</h4>
                  <p className="text-sm text-gray-600">Compression ignition internal combustion engines</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart JJJJ</h4>
                  <p className="text-sm text-gray-600">Stationary spark ignition internal combustion engines</p>
                </div>
              </div>
            </div>

            {/* NESHAP */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 63 - NESHAP Requirements</h3>
              <p className="text-gray-600 mb-4">Hazardous Air Pollutant standards applicable to oil and gas operations:</p>
              <div className="space-y-3">
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart HH - Oil and Natural Gas Production</h4>
                    <p className="text-sm text-gray-600">Standards for glycol dehydration units and storage vessels</p>
                  </div>
                </div>
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart ZZZZ - Stationary RICE</h4>
                    <p className="text-sm text-gray-600">Reciprocating internal combustion engines at major and area sources</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alaska Specific */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-amber-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Alaska-Specific Regulations</h3>
              <p className="text-gray-600 mb-4">
                Alaska has developed unique regulatory approaches for oil and gas operations:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Minor General Permit MG2 for Portable Oil and Gas Operations (POGO)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Construction phase air emissions requirements for oil fields</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Intermittently used oil field support equipment policies</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Non-road engines in oil and gas operations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Advantage */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategic Advantage</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Raven Environmental bridges the gap between the regulator and the rig. We provide the certainty that comes from knowing the regulations from the inside out.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Leverage Insider Expertise?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Work with consultants who understand Alaska's General Permit landscape because they helped create it. Ensure your operations stay compliant and mobile.
          </p>
          <Link
            to="/contact/oil-and-gas"
            className="inline-flex items-center px-8 py-4 bg-white text-amber-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Contact Our Team
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OilAndGas;
